import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  ShoppingCart as CartIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface TaxDetail {
  name: string;
  rate: number;
  amount: number;
}

interface ChargeDetails {
  serviceType: 'extension' | 'phone_number' | 'softphone';
  serviceId: string;
  description: string;
  monthlyRate: number;
  proratedDays?: number;
  proratedAmount?: number;
  currentMonthCharge?: number;
  nextMonthCharge?: number;
  subtotal?: number;
  taxDetails?: TaxDetail[];
  totalTax?: number;
  totalCharge: number;
  currentBalance: number;
  newBalance: number;
  sufficientFunds: boolean;
  billingPeriod?: string;
  includedInPackage?: boolean;
  province?: string;
  provinceName?: string;
}

interface ChargePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onAddFunds: () => void;
  chargeDetails: ChargeDetails | null;
  loading?: boolean;
  error?: string | null;
}

const ChargePreviewDialog: React.FC<ChargePreviewDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onAddFunds,
  chargeDetails,
  loading = false,
  error = null,
}) => {
  if (!chargeDetails) return null;

  const getServiceIcon = (type: string) => {
    switch (type) {
      case 'extension':
        return '📞';
      case 'phone_number':
        return '☎️';
      case 'softphone':
        return '📱';
      default:
        return '📋';
    }
  };

  const getServiceLabel = (type: string) => {
    switch (type) {
      case 'extension':
        return 'Extension';
      case 'phone_number':
        return 'Phone Number';
      case 'softphone':
        return 'Softphone';
      default:
        return 'Service';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" component="span">
            {getServiceIcon(chargeDetails.serviceType)} Billing Preview
          </Typography>
          <Chip
            label={getServiceLabel(chargeDetails.serviceType)}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Service Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {chargeDetails.description}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monthly Rate: {formatCurrency(chargeDetails.monthlyRate)}
          </Typography>
        </Box>

        {chargeDetails.includedInPackage ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              This service is included in your package at no additional charge!
            </Typography>
          </Alert>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Charges Breakdown
              </Typography>
              
              {chargeDetails.serviceType === 'phone_number' && chargeDetails.nextMonthCharge ? (
                <>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Current Month ({chargeDetails.proratedDays} days):
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(chargeDetails.currentMonthCharge || 0)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Next Month (Full):</Typography>
                    <Typography variant="body2">
                      {formatCurrency(chargeDetails.nextMonthCharge)}
                    </Typography>
                  </Box>
                </>
              ) : chargeDetails.proratedDays ? (
                <>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      Prorated for {chargeDetails.proratedDays} days:
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(chargeDetails.proratedAmount || chargeDetails.subtotal || chargeDetails.totalCharge)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Monthly Charge:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(chargeDetails.subtotal || chargeDetails.totalCharge)}
                  </Typography>
                </Box>
              )}
              
              {/* Tax Details */}
              {chargeDetails.taxDetails && chargeDetails.taxDetails.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">
                      {formatCurrency(chargeDetails.subtotal || chargeDetails.totalCharge - (chargeDetails.totalTax || 0))}
                    </Typography>
                  </Box>
                  {chargeDetails.taxDetails.map((tax, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {tax.name} ({tax.rate}%):
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(tax.amount)}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
              
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" fontWeight="bold">
                  Total Due:
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {formatCurrency(chargeDetails.totalCharge)}
                </Typography>
              </Box>
            </Paper>

            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <BalanceIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Account Balance
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Current Balance:</Typography>
                <Typography variant="body2">
                  {formatCurrency(chargeDetails.currentBalance)}
                </Typography>
              </Box>
              
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Service Charge:</Typography>
                <Typography variant="body2" color="error">
                  -{formatCurrency(chargeDetails.totalCharge)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" fontWeight="bold">
                  New Balance:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={chargeDetails.newBalance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(chargeDetails.newBalance)}
                </Typography>
              </Box>
            </Box>

            {!chargeDetails.sufficientFunds && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You need to add {formatCurrency(Math.abs(chargeDetails.newBalance))} to your account 
                  to complete this order.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        {chargeDetails.includedInPackage ? (
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            Create {getServiceLabel(chargeDetails.serviceType)}
          </Button>
        ) : chargeDetails.sufficientFunds ? (
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CartIcon />}
          >
            Buy Now
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onAddFunds}
            disabled={loading}
            color="warning"
            startIcon={<WarningIcon />}
          >
            Add Funds
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChargePreviewDialog;